import React, { useState, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import { useTranslation } from 'react-i18next';
import FolderExternalChecksItemSummaryComponent from './ItemSummaryComponent';
import FolderExternalChecksItemContentComponent from './ItemContentComponent';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import { useMutation, useSubscription } from '@apollo/client';
import { UPDATE_FOLDER_EXTERNAL_CHECK_RESULT } from './mutations/index';
import { FOLDER_EXTERNAL_CHECK_UPDATED } from './subscriptions/index';
import PropTypes from 'prop-types';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { RightsContext } from 'providers/RightsContext';
import { CollapseCard } from '@totalsoft/rocket-ui';

const FolderExternalChecksItemComponent = ({ check, index, list, onUpdateData }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const { decisionExternalChecksWrite, decisionExternalChecksRead } = useContext(RightsContext);

  const dispatch = useDispatch();
  const [t] = useTranslation();

  const [checkLens, checkDirtyInfo, resetLens] = useChangeTrackingLens(check);

  const handleToggle = () => setExpanded(prevState => !prevState);
  const externalCheck = checkLens |> get;

  const onResultChange = value => (checkLens.result |> set)(value);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  useSubscription(FOLDER_EXTERNAL_CHECK_UPDATED, {
    variables: { documentVerificationId: check.documentVerificationId },
    onSubscriptionData: ({
      subscriptionData: {
        data: { folderExternalCheckResultUpdated }
      }
    }) => {
      if (folderExternalCheckResultUpdated) {
        setSaving(false);
        const { documentVerificationId } = folderExternalCheckResultUpdated;
        if (check.documentVerificationId === documentVerificationId)
          dispatch(showInfo(t('Folder.VerificationsSummary.ExternalCheckUpdated')));
      }
    }
  });

  const [updateFolderExternalCheckResult] = useMutation(UPDATE_FOLDER_EXTERNAL_CHECK_RESULT, {
    onCompleted: data => {
      const {
        updateFolderExternalCheckResult: { correlationId }
      } = data;
      storeKey({ correlationId });
    }
  });

  const handleFolderExternalCheckResultUpdate = useCallback(
    (event, result) => {
      event.stopPropagation();

      if (!check) return;

      updateFolderExternalCheckResult({
        variables: {
          folderExternalCheck: {
            documentVerificationId: check.documentVerificationId,
            result: result
          }
        }
      });

      setSaving(true);

      const externalCheckList = [...list];

      const item = externalCheckList.find(item => item.documentVerificationId === check.documentVerificationId);
      var index = externalCheckList.indexOf(item);

      if (index !== -1) {
        const newItem = { ...item, result };
        externalCheckList[index] = newItem;
      }

      onUpdateData(externalCheckList);

      const newCheck = { ...check, result };
      resetLens(newCheck);
    },
    [check, list, onUpdateData, resetLens, updateFolderExternalCheckResult]
  );

  const folderExternalChecksItemSummary = (
    <FolderExternalChecksItemSummaryComponent
      check={externalCheck}
      expanded={expanded}
      index={index}
      dirtyInfo={checkDirtyInfo}
      saving={saving}
      onSave={handleFolderExternalCheckResultUpdate}
      decisionExternalChecksWrite={decisionExternalChecksWrite}
    />
  );

  const folderExternalChecksItemContent = (
    <FolderExternalChecksItemContentComponent check={externalCheck} onResultChange={onResultChange} />
  );

  return (
    <>
      {decisionExternalChecksRead && (
        <CollapseCard expanded={expanded} onToggle={handleToggle} title={folderExternalChecksItemSummary} toggleOnHeaderClick>
          {folderExternalChecksItemContent}
        </CollapseCard>
      )}
    </>
  );
};

FolderExternalChecksItemComponent.propTypes = {
  check: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  list: PropTypes.array.isRequired,
  onUpdateData: PropTypes.func.isRequired
};

export default FolderExternalChecksItemComponent;
